import ConductionModal from '@/components/modals/ConductionModal';
import { AuthContext } from '@/context/AuthContext';
import { Anchor, Button, Checkbox, Group, PasswordInput, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconLock, IconUser } from '@tabler/icons-react';
import { useCallback, useContext, useEffect, useState } from 'react';
import UserRegistrationModal from './UserRegistrationForm';
import { useNavigate } from 'react-router-dom';

const GUEST_LOGIN_GUARD_KEY = 'guest-login-attempted';

interface UserLoginFormProps {
	onClose: () => void;
}

const UserLoginForm: React.FC<UserLoginFormProps> = ({ onClose }) => {
	const auth = useContext(AuthContext);
	if (auth === undefined) {
		throw new Error("Modals must be a child of AuthContext.");
	}

	const [registrationModalOpen, setRegistrationModalOpen] = useState(false);

	// If the current path is "guest-login", then we should log the user in as a guest automatically
	const [guestLoggingIn, setGuestLoggingIn] = useState(false);
	const navigate = useNavigate();

	const loginAsGuest = useCallback(async () => {
		setGuestLoggingIn(true);
		try {
			const ok = await auth.guestLogin(true);
			if (ok) {
				// Clear the guard so future explicit visits can log in again if desired
				sessionStorage.removeItem(GUEST_LOGIN_GUARD_KEY);
				onClose();
				auth.updatePreTourChecklist('login-closed', true);
				return;
			}
		} finally {
			setGuestLoggingIn(false);
		}
		// If login failed, allow retry by clearing the guard
		sessionStorage.removeItem(GUEST_LOGIN_GUARD_KEY);
	}, [auth, onClose]);

	useEffect(() => {
		auth.updatePreTourChecklist("login-closed", false);
	}, []);

	useEffect(() => {
		const onGuestLoginRoute = location.pathname.startsWith('/guest-login');

		if (!onGuestLoginRoute) {
			// Leaving the route, reset guard to allow future auto-logins
			sessionStorage.removeItem(GUEST_LOGIN_GUARD_KEY);
			return;
		}

		// Navigate away from the guest-login route after handling
		navigate('/', { replace: true });

		// If already authenticated, do nothing
		if (auth.user) return;

		// Prevent duplicate attempts in React 18 Strict Mode dev double-mount
		const alreadyAttempted = sessionStorage.getItem(GUEST_LOGIN_GUARD_KEY) === '1';
		if (alreadyAttempted || guestLoggingIn) return;

		// Mark before calling the async login to block the second Strict Mode pass
		sessionStorage.setItem(GUEST_LOGIN_GUARD_KEY, '1');
		void loginAsGuest();
	}, [location.pathname, auth.user, guestLoggingIn, loginAsGuest]);

	const form = useForm({
		mode: 'uncontrolled',
		initialValues: {
			usernameOrEmail: '',
			password: '',
			rememberMe: true,
		}
	});

	const handleSubmit = async ({ usernameOrEmail, password, rememberMe }: typeof form.values) => {
		if (await auth.login(usernameOrEmail, password, rememberMe)) {
			onClose();
			auth.updatePreTourChecklist("login-closed", true);
		}
	};
	
	return (
		<ConductionModal
			onClose={() => {
				onClose();
				auth.updatePreTourChecklist("login-closed", true);
			}}
			title="Log In"
			size="md"
			withCloseButton={false}
		>
			<Stack>
				<UserRegistrationModal
					opened={registrationModalOpen}
					onClose={() => setRegistrationModalOpen(false)}
				/>

				<form onSubmit={form.onSubmit(handleSubmit)}>
					<Stack>
						<TextInput
							label={
								<Group>
									<IconUser size={20} />
									<Text>Username (or Email Address)</Text>
								</Group>
							}
							disabled={form.submitting || guestLoggingIn}
							{...form.getInputProps('usernameOrEmail')}
							required
							withAsterisk={false}
						/>

						<PasswordInput
							label={
								<Group>
									<IconLock size={20} />
									<Text>Password</Text>
									<Text></Text>
								</Group>
							}
							disabled={form.submitting || guestLoggingIn}
							{...form.getInputProps('password')}
							required
							withAsterisk={false}
						/>

						<Checkbox
							label="Remember Me"
							{...form.getInputProps('rememberMe', { type: 'checkbox' })}
						/>

						<Button
							type="submit"
							radius="xl"
							size='sm'
							loading={form.submitting || guestLoggingIn}
							color="red"
						>
							Log In
						</Button>
					</Stack>
				</form>

				<Stack
					align="center"
				>
					<Button
						onClick={() => setRegistrationModalOpen(true)}
						size="s"
						variant="outline"
					>
						Don&apos;t have an account? Register by clicking here!
					</Button>
					<Anchor
						component="button"
						type="button"
						onClick={loginAsGuest}
						size="s"
					>
						Or try out the app in guest mode.
					</Anchor>
				</Stack>
			</Stack>
		</ConductionModal>
	);
};

export default UserLoginForm;
